/**
 * Headscale Administration Component
 *
 * Provides a unified interface for managing Headscale VPN:
 * - Health status and connection overview
 * - Pre-auth key management
 * - Route management
 * - Node registration flow
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

interface HeadscaleHealth {
  healthy: boolean;
  latencyMs: number;
  version?: string;
  error?: string;
}

interface HeadscaleSummary {
  connected: boolean;
  totalNodes: number;
  onlineNodes: number;
  offlineNodes: number;
  preAuthKeys: {
    total: number;
    active: number;
    expired: number;
  };
  lastSync?: string;
}

interface PreAuthKey {
  id: string;
  key?: string;
  user: string;
  reusable: boolean;
  ephemeral: boolean;
  used: boolean;
  expiration: string;
  createdAt: string;
  aclTags?: string[];
}

interface Route {
  id: string;
  node: {
    id: string;
    name: string;
  };
  prefix: string;
  advertised: boolean;
  enabled: boolean;
  isPrimary: boolean;
}

type TabType = 'overview' | 'keys' | 'routes' | 'register';

// =============================================================================
// Sub-components
// =============================================================================

function StatusCard({
  title,
  value,
  subtitle,
  status,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  status?: 'success' | 'warning' | 'error' | 'neutral';
}) {
  const statusColors = {
    success: 'border-green-500/30 bg-green-500/10',
    warning: 'border-yellow-500/30 bg-yellow-500/10',
    error: 'border-red-500/30 bg-red-500/10',
    neutral: 'border-discord-light bg-discord-darker',
  };

  const valueColors = {
    success: 'text-green-400',
    warning: 'text-yellow-400',
    error: 'text-red-400',
    neutral: 'text-white',
  };

  return (
    <div className={`rounded-lg border p-4 ${statusColors[status || 'neutral']}`}>
      <div className="text-sm text-gray-400">{title}</div>
      <div className={`mt-1 text-2xl font-bold ${valueColors[status || 'neutral']}`}>
        {value}
      </div>
      {subtitle && <div className="mt-1 text-xs text-gray-500">{subtitle}</div>}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? 'bg-discord-blurple text-white'
          : 'bg-discord-light text-gray-300 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

// =============================================================================
// Overview Tab
// =============================================================================

function OverviewTab({
  health,
  summary,
  onRefresh,
  isLoading,
}: {
  health: HeadscaleHealth | null;
  summary: HeadscaleSummary | null;
  onRefresh: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-6">
      {/* Health Status */}
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Connection Health</h3>
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="rounded-md bg-discord-light px-3 py-1.5 text-sm text-gray-300 hover:text-white transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatusCard
            title="Status"
            value={health?.healthy ? 'Connected' : 'Disconnected'}
            status={health?.healthy ? 'success' : 'error'}
          />
          <StatusCard
            title="Latency"
            value={health?.latencyMs ? `${health.latencyMs}ms` : 'N/A'}
            status={
              health?.latencyMs
                ? health.latencyMs < 100
                  ? 'success'
                  : health.latencyMs < 500
                    ? 'warning'
                    : 'error'
                : 'neutral'
            }
          />
          <StatusCard
            title="Version"
            value={health?.version || 'Unknown'}
            status="neutral"
          />
          <StatusCard
            title="Last Sync"
            value={
              summary?.lastSync
                ? new Date(summary.lastSync).toLocaleTimeString()
                : 'Never'
            }
            status="neutral"
          />
        </div>

        {health?.error && (
          <div className="mt-4 rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-400">
            {health.error}
          </div>
        )}
      </div>

      {/* Node Statistics */}
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Node Statistics</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <StatusCard
            title="Total Nodes"
            value={summary?.totalNodes ?? 0}
            status="neutral"
          />
          <StatusCard
            title="Online"
            value={summary?.onlineNodes ?? 0}
            status={summary?.onlineNodes ? 'success' : 'neutral'}
          />
          <StatusCard
            title="Offline"
            value={summary?.offlineNodes ?? 0}
            status={summary?.offlineNodes ? 'warning' : 'neutral'}
          />
        </div>
      </div>

      {/* Pre-Auth Key Statistics */}
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Pre-Auth Keys</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <StatusCard
            title="Total Keys"
            value={summary?.preAuthKeys?.total ?? 0}
            status="neutral"
          />
          <StatusCard
            title="Active"
            value={summary?.preAuthKeys?.active ?? 0}
            status={summary?.preAuthKeys?.active ? 'success' : 'neutral'}
          />
          <StatusCard
            title="Expired"
            value={summary?.preAuthKeys?.expired ?? 0}
            status={summary?.preAuthKeys?.expired ? 'warning' : 'neutral'}
          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Pre-Auth Keys Tab
// =============================================================================

function KeysTab({
  keys,
  onCreateKey,
  onExpireKey,
  isLoading,
  isCreating,
}: {
  keys: PreAuthKey[];
  onCreateKey: (options: { reusable: boolean; ephemeral: boolean; expiration: string }) => void;
  onExpireKey: (keyId: string) => void;
  isLoading: boolean;
  isCreating: boolean;
}) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [reusable, setReusable] = useState(false);
  const [ephemeral, setEphemeral] = useState(false);
  const [expiration, setExpiration] = useState('24h');
  const [newKey, setNewKey] = useState<string | null>(null);

  const handleCreate = async () => {
    // Call the parent's onCreateKey - in a real implementation,
    // this would return the created key for display
    onCreateKey({ reusable, ephemeral, expiration });
    // Note: The actual key display would need the API to return the key
    // For now, just reset the form
    setShowCreateForm(false);
    setNewKey(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="space-y-6">
      {/* Create Key Section */}
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Create Pre-Auth Key</h3>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/80 transition-colors"
          >
            {showCreateForm ? 'Cancel' : 'New Key'}
          </button>
        </div>

        {showCreateForm && (
          <div className="space-y-4 border-t border-discord-light pt-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={reusable}
                  onChange={(e) => setReusable(e.target.checked)}
                  className="h-4 w-4 rounded border-discord-light bg-discord-darkest text-discord-blurple focus:ring-discord-blurple"
                />
                <div>
                  <span className="text-sm font-medium text-white">Reusable</span>
                  <p className="text-xs text-gray-500">Key can be used multiple times</p>
                </div>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={ephemeral}
                  onChange={(e) => setEphemeral(e.target.checked)}
                  className="h-4 w-4 rounded border-discord-light bg-discord-darkest text-discord-blurple focus:ring-discord-blurple"
                />
                <div>
                  <span className="text-sm font-medium text-white">Ephemeral</span>
                  <p className="text-xs text-gray-500">Nodes auto-delete when offline</p>
                </div>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Expiration
              </label>
              <select
                value={expiration}
                onChange={(e) => setExpiration(e.target.value)}
                className="w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple"
              >
                <option value="1h">1 Hour</option>
                <option value="24h">24 Hours</option>
                <option value="7d">7 Days</option>
                <option value="30d">30 Days</option>
                <option value="90d">90 Days</option>
              </select>
            </div>

            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="w-full rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isCreating ? 'Creating...' : 'Generate Key'}
            </button>
          </div>
        )}

        {newKey && (
          <div className="mt-4 rounded-md bg-green-500/10 border border-green-500/30 p-4">
            <div className="text-sm font-medium text-green-400 mb-2">Key Generated!</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-discord-darkest px-3 py-2 text-sm text-white font-mono break-all">
                {newKey}
              </code>
              <button
                onClick={() => copyToClipboard(newKey)}
                className="rounded-md bg-discord-light px-3 py-2 text-sm text-gray-300 hover:text-white transition-colors"
              >
                Copy
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Save this key now - it won't be shown again!
            </p>
          </div>
        )}
      </div>

      {/* Keys List */}
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Active Keys</h3>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-discord-blurple border-t-transparent" />
          </div>
        ) : keys.length === 0 ? (
          <div className="py-8 text-center text-gray-400">
            No pre-auth keys found. Create one to register new nodes.
          </div>
        ) : (
          <div className="space-y-3">
            {keys.map((key) => {
              const isExpired = new Date(key.expiration) < new Date();
              return (
                <div
                  key={key.id}
                  className={`rounded-md border p-4 ${
                    isExpired
                      ? 'border-red-500/30 bg-red-500/5'
                      : key.used && !key.reusable
                        ? 'border-yellow-500/30 bg-yellow-500/5'
                        : 'border-discord-light bg-discord-dark'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-white">
                          {key.id.substring(0, 12)}...
                        </span>
                        {key.reusable && (
                          <span className="rounded-full bg-blue-500/20 px-2 py-0.5 text-xs text-blue-400">
                            Reusable
                          </span>
                        )}
                        {key.ephemeral && (
                          <span className="rounded-full bg-purple-500/20 px-2 py-0.5 text-xs text-purple-400">
                            Ephemeral
                          </span>
                        )}
                        {isExpired && (
                          <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-xs text-red-400">
                            Expired
                          </span>
                        )}
                        {key.used && !key.reusable && !isExpired && (
                          <span className="rounded-full bg-yellow-500/20 px-2 py-0.5 text-xs text-yellow-400">
                            Used
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        User: {key.user} · Created: {new Date(key.createdAt).toLocaleDateString()}
                        {' · '}Expires: {new Date(key.expiration).toLocaleString()}
                      </div>
                    </div>
                    {!isExpired && (
                      <button
                        onClick={() => onExpireKey(key.id)}
                        className="rounded-md bg-red-600/20 px-3 py-1 text-xs text-red-400 hover:bg-red-600/30 transition-colors"
                      >
                        Expire
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Routes Tab
// =============================================================================

function RoutesTab({
  routes,
  onToggleRoute,
  isLoading,
}: {
  routes: Route[];
  onToggleRoute: (routeId: string, enable: boolean) => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Network Routes</h3>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-discord-blurple border-t-transparent" />
          </div>
        ) : routes.length === 0 ? (
          <div className="py-8 text-center text-gray-400">
            No routes found. Routes are advertised by connected nodes.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-discord-light text-left text-sm text-gray-400">
                  <th className="pb-3 pr-4">Prefix</th>
                  <th className="pb-3 pr-4">Node</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3 pr-4">Primary</th>
                  <th className="pb-3">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {routes.map((route) => (
                  <tr key={route.id} className="border-b border-discord-light/50">
                    <td className="py-3 pr-4">
                      <code className="rounded bg-discord-darkest px-2 py-1 text-white">
                        {route.prefix}
                      </code>
                    </td>
                    <td className="py-3 pr-4 text-gray-300">{route.node.name}</td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          route.enabled
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}
                      >
                        {route.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      {route.isPrimary && (
                        <span className="inline-flex items-center rounded-full bg-blue-500/20 px-2 py-1 text-xs font-medium text-blue-400">
                          Primary
                        </span>
                      )}
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => onToggleRoute(route.id, !route.enabled)}
                        className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                          route.enabled
                            ? 'bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30'
                            : 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                        }`}
                      >
                        {route.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Register Node Tab
// =============================================================================

function RegisterTab({
  onGenerateKey,
  isGenerating,
}: {
  onGenerateKey: () => Promise<string | null>;
  isGenerating: boolean;
}) {
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    const key = await onGenerateKey();
    if (key) {
      setGeneratedKey(key);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCommand = (key: string) => {
    const command = `docker run -d --name tailscale-client \\
  --cap-add=NET_ADMIN \\
  --cap-add=SYS_MODULE \\
  -v /dev/net/tun:/dev/net/tun \\
  -e TS_AUTHKEY=${key} \\
  -e TS_STATE_DIR=/var/lib/tailscale \\
  tailscale/tailscale:latest`;
    navigator.clipboard.writeText(command);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Register New Remote Node</h3>
        <p className="text-gray-400 mb-6">
          Follow these steps to connect a new compute node to the Headscale VPN network.
        </p>

        {/* Step 1: Generate Key */}
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-discord-blurple text-white font-bold">
              1
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-white">Generate Pre-Auth Key</h4>
              <p className="text-sm text-gray-400 mt-1">
                Generate a one-time key to authenticate your node.
              </p>
              {!generatedKey ? (
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="mt-3 rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/80 transition-colors disabled:opacity-50"
                >
                  {isGenerating ? 'Generating...' : 'Generate Key'}
                </button>
              ) : (
                <div className="mt-3 rounded-md bg-green-500/10 border border-green-500/30 p-3">
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded bg-discord-darkest px-3 py-2 text-sm text-white font-mono break-all">
                      {generatedKey}
                    </code>
                    <button
                      onClick={() => copyToClipboard(generatedKey)}
                      className="rounded-md bg-discord-light px-3 py-2 text-sm text-gray-300 hover:text-white transition-colors"
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Step 2: Install Tailscale */}
          <div className="flex items-start gap-4 pt-4 border-t border-discord-light">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-discord-light text-gray-400 font-bold">
              2
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-white">Install Tailscale on Remote Node</h4>
              <p className="text-sm text-gray-400 mt-1">
                Run the following Docker command on your remote node:
              </p>
              <div className="mt-3 rounded-md bg-discord-darkest p-4">
                <pre className="text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">
{`docker run -d --name tailscale-client \\
  --cap-add=NET_ADMIN \\
  --cap-add=SYS_MODULE \\
  -v /dev/net/tun:/dev/net/tun \\
  -e TS_AUTHKEY=${generatedKey || '<YOUR_KEY>'} \\
  -e TS_STATE_DIR=/var/lib/tailscale \\
  tailscale/tailscale:latest`}
                </pre>
                {generatedKey && (
                  <button
                    onClick={() => copyCommand(generatedKey)}
                    className="mt-2 rounded-md bg-discord-light px-3 py-1.5 text-xs text-gray-300 hover:text-white transition-colors"
                  >
                    Copy Command
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Step 3: Register in Dashboard */}
          <div className="flex items-start gap-4 pt-4 border-t border-discord-light">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-discord-light text-gray-400 font-bold">
              3
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-white">Register Node in Dashboard</h4>
              <p className="text-sm text-gray-400 mt-1">
                Once connected, go to the Node Dashboard and register the node with its
                Headscale IP address and hardware details.
              </p>
              <a
                href="/admin/nodes"
                className="mt-3 inline-flex items-center rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
              >
                Go to Node Dashboard
                <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Troubleshooting */}
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Troubleshooting</h3>
        <div className="space-y-3 text-sm text-gray-400">
          <div>
            <strong className="text-white">Node not appearing?</strong>
            <p>Check that the Tailscale container started successfully with <code className="text-gray-300">docker logs tailscale-client</code></p>
          </div>
          <div>
            <strong className="text-white">Connection refused?</strong>
            <p>Ensure port 443 is accessible from your remote node to the Headscale server.</p>
          </div>
          <div>
            <strong className="text-white">Key expired?</strong>
            <p>Generate a new pre-auth key and restart the Tailscale container with the new key.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function HeadscaleAdmin() {
  useAuth();

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [health, setHealth] = useState<HeadscaleHealth | null>(null);
  const [summary, setSummary] = useState<HeadscaleSummary | null>(null);
  const [keys, setKeys] = useState<PreAuthKey[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    const apiClient = getApiClient();
    try {
      const result = await apiClient.get<HeadscaleHealth>('/nodes/headscale/health');
      if (isSuccess(result)) {
        setHealth(result.data);
      }
    } catch {
      setHealth({ healthy: false, latencyMs: 0, error: 'Failed to connect' });
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    const apiClient = getApiClient();
    try {
      const result = await apiClient.get<HeadscaleSummary>('/nodes/headscale/summary');
      if (isSuccess(result)) {
        setSummary(result.data);
      }
    } catch {
      // Ignore
    }
  }, []);

  const fetchKeys = useCallback(async () => {
    const apiClient = getApiClient();
    try {
      const result = await apiClient.get<{ keys: PreAuthKey[] }>('/nodes/headscale/keys');
      if (isSuccess(result)) {
        setKeys(result.data.keys || []);
      }
    } catch {
      // Ignore - API may not exist yet
    }
  }, []);

  const fetchRoutes = useCallback(async () => {
    const apiClient = getApiClient();
    try {
      const result = await apiClient.get<{ routes: Route[] }>('/nodes/headscale/routes');
      if (isSuccess(result)) {
        setRoutes(result.data.routes || []);
      }
    } catch {
      // Ignore - API may not exist yet
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    await Promise.all([fetchHealth(), fetchSummary(), fetchKeys(), fetchRoutes()]);
    setIsLoading(false);
  }, [fetchHealth, fetchSummary, fetchKeys, fetchRoutes]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleCreateKey = useCallback(
    async (options: { reusable: boolean; ephemeral: boolean; expiration: string }) => {
      setIsCreatingKey(true);
      const apiClient = getApiClient();
      try {
        const result = await apiClient.post<{ key: PreAuthKey }>('/nodes/headscale/keys', options);
        if (isSuccess(result)) {
          await fetchKeys();
        } else {
          setError('Failed to create key');
        }
      } catch {
        setError('Failed to create key');
      } finally {
        setIsCreatingKey(false);
      }
    },
    [fetchKeys]
  );

  const handleExpireKey = useCallback(
    async (keyId: string) => {
      const apiClient = getApiClient();
      try {
        await apiClient.delete(`/nodes/headscale/keys/${keyId}`);
        await fetchKeys();
      } catch {
        setError('Failed to expire key');
      }
    },
    [fetchKeys]
  );

  const handleToggleRoute = useCallback(
    async (routeId: string, enable: boolean) => {
      const apiClient = getApiClient();
      try {
        await apiClient.post(`/nodes/headscale/routes/${routeId}/${enable ? 'enable' : 'disable'}`);
        await fetchRoutes();
      } catch {
        setError('Failed to update route');
      }
    },
    [fetchRoutes]
  );

  const handleGenerateRegistrationKey = useCallback(async (): Promise<string | null> => {
    setIsCreatingKey(true);
    const apiClient = getApiClient();
    try {
      const result = await apiClient.post<{ key: string }>('/nodes/headscale/keys', {
        reusable: false,
        ephemeral: false,
        expiration: '24h',
      });
      if (isSuccess(result)) {
        await fetchKeys();
        return result.data.key;
      }
      return null;
    } catch {
      setError('Failed to generate key');
      return null;
    } finally {
      setIsCreatingKey(false);
    }
  }, [fetchKeys]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Headscale VPN</h1>
          <p className="mt-1 text-gray-400">
            Manage VPN network, pre-auth keys, routes, and node registration
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-500/10 border border-red-500/30 p-4 text-red-400">
            <div className="flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-sm hover:text-red-300">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            Overview
          </TabButton>
          <TabButton active={activeTab === 'keys'} onClick={() => setActiveTab('keys')}>
            Pre-Auth Keys
          </TabButton>
          <TabButton active={activeTab === 'routes'} onClick={() => setActiveTab('routes')}>
            Routes
          </TabButton>
          <TabButton active={activeTab === 'register'} onClick={() => setActiveTab('register')}>
            Register Node
          </TabButton>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab
            health={health}
            summary={summary}
            onRefresh={fetchAll}
            isLoading={isLoading}
          />
        )}
        {activeTab === 'keys' && (
          <KeysTab
            keys={keys}
            onCreateKey={handleCreateKey}
            onExpireKey={handleExpireKey}
            isLoading={isLoading}
            isCreating={isCreatingKey}
          />
        )}
        {activeTab === 'routes' && (
          <RoutesTab routes={routes} onToggleRoute={handleToggleRoute} isLoading={isLoading} />
        )}
        {activeTab === 'register' && (
          <RegisterTab onGenerateKey={handleGenerateRegistrationKey} isGenerating={isCreatingKey} />
        )}
      </div>
    </Layout>
  );
}

export default HeadscaleAdmin;
